import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ChevronDown, ChevronUp, Search, Upload, Sparkles, Link2, BookOpen, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Descriptive labels for puzzle pieces in admin interfaces
const PIECE_LABELS = {
  'corner-1': { chip: 'C1: 2Thess2', card: 'Corner 1 — 2 Thessalonians 2:1-3', lesson: 'Lesson 4' },
  'corner-2': { chip: 'C2: Matt24', card: 'Corner 2 — Matthew 24:29-31', lesson: 'Lesson 5' },
  'corner-3': { chip: 'C3: 1Cor15', card: 'Corner 3 — 1 Corinthians 15:51-52', lesson: 'Lesson 6' },
  'corner-4': { chip: 'C4: Rev20', card: 'Corner 4 — Revelation 20:4-6', lesson: 'Lesson 7' },
  'border-1': { chip: 'B1: Rev11 (7th Trumpet)', card: 'Border 1 — Revelation 11:15,18', lesson: 'Lesson 8' },
  'border-2': { chip: 'B2: 1Thess4 (Caught Up)', card: 'Border 2 — 1 Thessalonians 4:17', lesson: 'Lesson 9' },
  'border-3': { chip: 'B3: 2Thess1 (Rest)', card: 'Border 3 — 2 Thessalonians 1:7-8', lesson: 'Lesson 10' },
  'border-4': { chip: 'B4: 2Thess2 (Destroyed)', card: 'Border 4 — 2 Thessalonians 2:3,8', lesson: 'Lesson 11' },
  'border-5': { chip: 'B5: John5 (Two Resurrections)', card: 'Border 5 — John 5:28-29', lesson: 'Lesson 12' },
  'border-6': { chip: 'B6: Rev16 (Thief/Keystone)', card: 'Border 6 — Revelation 16:15', lesson: 'Lesson 13' },
  'border-7': { chip: 'L3: Recapitulation', card: 'Legend 3 — Recapitulation', lesson: 'Lesson 3' },
  'border-8': { chip: 'L2: Blueprints', card: 'Legend 2 — Blueprints (Mo\'edim)', lesson: 'Lesson 2' },
};

function getPieceChipLabel(piece) {
  const label = PIECE_LABELS[piece.piece_id];
  if (label) return label.chip;
  // Fallback for unknown pieces
  const prefix = piece.piece_type === 'corner' ? 'C' : piece.piece_type === 'border' ? 'B' : 'I';
  return `${prefix}${piece.number}: ${piece.keyword || piece.reference?.substring(0, 15) || piece.title}`;
}

function getPieceCardLabel(piece) {
  const label = PIECE_LABELS[piece.piece_id];
  if (label) return label.card;
  return `${piece.piece_type === 'corner' ? 'Corner' : 'Border'} ${piece.number} — ${piece.reference || piece.title}`;
}

function getPieceLessonLabel(piece) {
  const label = PIECE_LABELS[piece.piece_id];
  return label?.lesson || '';
}

const AdminAppendixEditor = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [appendices, setAppendices] = useState([]);
  const [puzzlePieces, setPuzzlePieces] = useState([]);
  const [specialChapters, setSpecialChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('appendices');
  const [uploading, setUploading] = useState(null);
  const [generating, setGenerating] = useState(null);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchAppendices(), fetchPuzzlePieces(), fetchSpecialChapters()]);
    setLoading(false);
  };

  const fetchAppendices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAppendices(data.appendices || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchPuzzlePieces = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices/puzzle-pieces`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPuzzlePieces(data.pieces || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchSpecialChapters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices/special-chapters`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSpecialChapters(data.chapters || []);
      }
    } catch (e) { console.error(e); }
  };

  const expandAppendix = async (chapterId) => {
    if (expandedId === chapterId) { setExpandedId(null); return; }
    setExpandedId(chapterId);
    if (!editData[chapterId]) {
      try {
        const res = await fetch(`${API_URL}/api/admin/appendices/${chapterId}`, { headers: headers(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setEditData(prev => ({ ...prev, [chapterId]: data }));
        }
      } catch (e) { console.error(e); }
    }
  };

  const updateField = (chapterId, field, value) => {
    setEditData(prev => ({
      ...prev,
      [chapterId]: { ...prev[chapterId], [field]: value }
    }));
  };

  const saveAppendix = async (chapterId) => {
    setSaving(chapterId);
    const data = editData[chapterId];
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices/${chapterId}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({
          content: data.content,
          title: data.title,
          laymen_summary: data.laymen_summary,
          assigned_pieces: data.assigned_pieces || [],
        }),
      });
      if (res.ok) {
        setSaveMsg('Saved!');
        fetchAppendices();
      } else setSaveMsg('Error saving');
    } catch (e) { setSaveMsg('Error'); }
    setSaving(null);
    setTimeout(() => setSaveMsg(''), 2000);
  };

  const generateSummary = async (chapterId) => {
    setGenerating(chapterId);
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices/${chapterId}/generate-summary`, {
        method: 'POST', headers: headers(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setEditData(prev => ({
          ...prev,
          [chapterId]: { ...prev[chapterId], laymen_summary: data.laymen_summary }
        }));
        setSaveMsg('Summary generated — edit as needed');
      }
    } catch (e) { setSaveMsg('Error generating'); }
    setGenerating(null);
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const uploadDocx = async (chapterId, file) => {
    setUploading(chapterId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const h = {};
      if (token) h['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/admin/appendices/${chapterId}/upload-docx`, {
        method: 'POST', headers: h, credentials: 'include', body: formData,
      });
      if (res.ok) {
        setSaveMsg('DOCX uploaded and parsed!');
        const full = await fetch(`${API_URL}/api/admin/appendices/${chapterId}`, { headers: headers(), credentials: 'include' });
        if (full.ok) {
          const data = await full.json();
          setEditData(prev => ({ ...prev, [chapterId]: data }));
        }
      } else {
        const err = await res.json();
        setSaveMsg(`Upload error: ${err.detail}`);
      }
    } catch (e) { setSaveMsg('Upload failed'); }
    setUploading(null);
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const togglePieceAssignment = (chapterId, pieceId) => {
    const current = editData[chapterId]?.assigned_pieces || [];
    const updated = current.includes(pieceId) ? current.filter(p => p !== pieceId) : [...current, pieceId];
    updateField(chapterId, 'assigned_pieces', updated);
  };

  const saveSpecialChapter = async (chapterType, title, content, laymenSummary) => {
    setSaving(chapterType);
    try {
      const res = await fetch(`${API_URL}/api/admin/appendices/special-chapters/${chapterType}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ title, content, laymen_summary: laymenSummary, chapter_type: chapterType }),
      });
      if (res.ok) { setSaveMsg('Special chapter saved!'); fetchSpecialChapters(); }
    } catch (e) { setSaveMsg('Error saving'); }
    setSaving(null);
    setTimeout(() => setSaveMsg(''), 2000);
  };

  const filtered = appendices.filter(a =>
    a.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="border-b border-slate-800 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-slate-200 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Content Manager</h1>
          </div>
          {saveMsg && (
            <Badge className="bg-amber-600 text-white animate-pulse">{saveMsg}</Badge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <div className="flex gap-1 border-b border-slate-800 mb-6">
          {[
            { id: 'appendices', label: 'Appendices', icon: FileText },
            { id: 'special', label: 'Special Chapters', icon: BookOpen },
            { id: 'assignments', label: 'Piece Assignments', icon: Link2 },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-300 hover:text-slate-300'
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-12">
        {/* ===== APPENDICES TAB ===== */}
        {tab === 'appendices' && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search appendices..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm focus:border-amber-500 focus:outline-none" />
              </div>
              <Badge variant="outline" className="text-slate-200">{filtered.length} appendices</Badge>
            </div>

            <div className="space-y-2">
              {filtered.map(app => {
                const isExpanded = expandedId === app.chapter_id;
                const data = editData[app.chapter_id] || {};
                const assignedCount = (data.assigned_pieces || app.assigned_pieces || []).length;
                const hasSummary = !!(data.laymen_summary || app.laymen_summary);

                return (
                  <Card key={app.chapter_id} className="bg-slate-900 border-slate-800" data-testid={`appendix-${app.chapter_id}`}>
                    <button onClick={() => expandAppendix(app.chapter_id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-mono text-amber-600">{app.section_order || '—'}</span>
                        <span className="text-sm font-medium truncate">{app.title}</span>
                        <div className="flex gap-1.5">
                          {assignedCount > 0 && (
                            <Badge className="bg-blue-900/50 text-blue-300 text-[10px]">
                              <Link2 className="w-3 h-3 mr-1" />{assignedCount} piece{assignedCount > 1 ? 's' : ''}
                            </Badge>
                          )}
                          {hasSummary && (
                            <Badge className="bg-green-900/50 text-green-300 text-[10px]">Summary</Badge>
                          )}
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-800 pt-4 space-y-4">
                        {/* Title */}
                        <div>
                          <label className="text-xs font-mono text-slate-300 uppercase tracking-wider mb-1 block">Title</label>
                          <input value={data.title || ''} onChange={e => updateField(app.chapter_id, 'title', e.target.value)}
                            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm focus:border-amber-500 focus:outline-none" />
                        </div>

                        {/* Scholar Content */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-mono text-slate-300 uppercase tracking-wider">Scholar Content</label>
                            <div className="flex items-center gap-2">
                              <label className="text-[10px] text-amber-500 cursor-pointer hover:text-amber-300 flex items-center gap-1">
                                <Upload className="w-3 h-3" />
                                {uploading === app.chapter_id ? 'Uploading...' : 'Upload DOCX'}
                                <input type="file" accept=".docx" className="hidden"
                                  onChange={e => { if (e.target.files[0]) uploadDocx(app.chapter_id, e.target.files[0]); }} />
                              </label>
                              <span className="text-[10px] text-slate-200">{(data.content || '').length} chars</span>
                            </div>
                          </div>
                          <textarea value={data.content || ''} onChange={e => updateField(app.chapter_id, 'content', e.target.value)}
                            rows={8}
                            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm font-serif leading-relaxed focus:border-amber-500 focus:outline-none resize-y" />
                        </div>

                        {/* Laymen Summary */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-mono text-slate-300 uppercase tracking-wider">Laymen Summary</label>
                            <button onClick={() => generateSummary(app.chapter_id)}
                              disabled={generating === app.chapter_id}
                              data-testid={`btn-generate-${app.chapter_id}`}
                              className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 disabled:opacity-50">
                              <Sparkles className="w-3 h-3" />
                              {generating === app.chapter_id ? 'Generating...' : 'Auto-Generate'}
                            </button>
                          </div>
                          <textarea value={data.laymen_summary || ''} onChange={e => updateField(app.chapter_id, 'laymen_summary', e.target.value)}
                            rows={4} placeholder="Write a simplified version for general readers..."
                            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm font-serif leading-relaxed focus:border-amber-500 focus:outline-none resize-y" />
                        </div>

                        {/* Puzzle Piece Assignment */}
                        <div>
                          <label className="text-xs font-mono text-slate-300 uppercase tracking-wider mb-2 block">Assign to Puzzle Pieces</label>
                          <div className="flex flex-wrap gap-2">
                            {puzzlePieces.map(p => {
                              const assigned = (data.assigned_pieces || []).includes(p.piece_id);
                              return (
                                <button key={p.piece_id} onClick={() => togglePieceAssignment(app.chapter_id, p.piece_id)}
                                  data-testid={`assign-${app.chapter_id}-${p.piece_id}`}
                                  className={`px-3 py-1.5 rounded-full text-xs font-mono transition-all ${
                                    assigned
                                      ? 'bg-amber-700 text-white border border-amber-500'
                                      : 'bg-slate-800 text-slate-200 border border-slate-700 hover:border-slate-500'
                                  }`}>
                                  {getPieceChipLabel(p)}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Save */}
                        <div className="flex justify-end">
                          <Button onClick={() => saveAppendix(app.chapter_id)} disabled={saving === app.chapter_id}
                            data-testid={`btn-save-${app.chapter_id}`}
                            className="bg-amber-600 hover:bg-amber-700 text-white">
                            <Save className="w-4 h-4 mr-2" />
                            {saving === app.chapter_id ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== SPECIAL CHAPTERS TAB ===== */}
        {tab === 'special' && (
          <div className="space-y-6">
            <p className="text-sm text-slate-200">These two chapters are always visible on the Mind Map and Treasure Map.</p>
            {['recapitulation', 'blueprints'].map(type => {
              const existing = specialChapters.find(c => c.chapter_type === type);
              const defaultTitle = type === 'recapitulation' ? 'The Recapitulation Method of Reading Revelation' : 'Blueprints: The Holy Days';

              return (
                <SpecialChapterEditor
                  key={type}
                  chapterType={type}
                  defaultTitle={defaultTitle}
                  existing={existing}
                  saving={saving === type}
                  onSave={(title, content, summary) => saveSpecialChapter(type, title, content, summary)}
                />
              );
            })}
          </div>
        )}

        {/* ===== ASSIGNMENTS TAB ===== */}
        {tab === 'assignments' && (
          <div className="space-y-6">
            <p className="text-sm text-slate-200 mb-4">Overview of which appendices are assigned to each puzzle piece.</p>
            {puzzlePieces.map(piece => {
              const assigned = appendices.filter(a => (a.assigned_pieces || []).includes(piece.piece_id));
              return (
                <Card key={piece.piece_id} className="bg-slate-900 border-slate-800 p-4" data-testid={`assignments-${piece.piece_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className={piece.piece_type === 'corner' ? 'bg-amber-700' : piece.piece_id === 'border-7' || piece.piece_id === 'border-8' ? 'bg-indigo-700' : 'bg-slate-700'}>
                      {getPieceCardLabel(piece)}
                    </Badge>
                    {getPieceLessonLabel(piece) && (
                      <span className="text-xs text-slate-300 font-mono">{getPieceLessonLabel(piece)}</span>
                    )}
                  </div>
                  {assigned.length === 0 ? (
                    <p className="text-xs text-slate-200 italic">No appendices assigned yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {assigned.map(a => (
                        <Badge key={a.chapter_id} variant="outline" className="text-slate-300 border-slate-600 text-xs">
                          {a.title}
                        </Badge>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Sub-component: Special Chapter Editor =====
function SpecialChapterEditor({ chapterType, defaultTitle, existing, saving, onSave }) {
  const [title, setTitle] = useState(existing?.title || defaultTitle);
  const [content, setContent] = useState(existing?.content || '');
  const [summary, setSummary] = useState(existing?.laymen_summary || '');

  return (
    <Card className="bg-slate-900 border-slate-800 p-5" data-testid={`special-${chapterType}`}>
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-amber-500" />
        <h3 className="font-bold capitalize">{chapterType === 'recapitulation' ? 'Recapitulation Method' : 'Blueprints: Holy Days'}</h3>
        {existing && <Badge className="bg-green-900/50 text-green-300 text-[10px]">Created</Badge>}
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-mono text-slate-300 uppercase tracking-wider mb-1 block">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm focus:border-amber-500 focus:outline-none" />
        </div>

        <div>
          <label className="text-xs font-mono text-slate-300 uppercase tracking-wider mb-1 block">Full Scholar Content</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={8}
            placeholder="Paste or write the full chapter content..."
            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm font-serif leading-relaxed focus:border-amber-500 focus:outline-none resize-y" />
        </div>

        <div>
          <label className="text-xs font-mono text-slate-300 uppercase tracking-wider mb-1 block">Laymen Summary</label>
          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={4}
            placeholder="Simplified version for general readers..."
            className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm font-serif leading-relaxed focus:border-amber-500 focus:outline-none resize-y" />
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onSave(title, content, summary)} disabled={saving}
            data-testid={`btn-save-special-${chapterType}`}
            className="bg-amber-600 hover:bg-amber-700 text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Chapter'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default AdminAppendixEditor;
